// Lucimix iframe 入口的握手：向父窗口要一次性票据。协议见
// docs/sdd/astock-agent-v08-lucimix-identity-billing/spec.md §5。
//
//   我方 → 父窗口  {type: "external-app:ready"}
//   父窗口 → 我方  票据（60 秒有效、只能用一次），两种信封都收：
//                    {type: "external-app:ticket", ticket}   ← 对接说明里写的
//                    {type: "janus:auth", token}             ← 测试环境实际部署的
//   2 秒没收到就重发 ready，最多发 3 次，仍没有就放弃。
//
// 【为什么两种都收】2026-09-20 在 Lucimix 测试环境（l-mlptest1.ghznai.cn:3005）读它实际
// 部署的前端 bundle：它先向自己后端 POST /gateway/external-app-ticket 拿到 {ticket}，
// 然后发的是 {type: "janus:auth", token: <那张票据>}——v2.0 的旧信封，和对接说明不一致。
// 只认说明里的格式会把真消息当无关消息丢掉，表现为「没有收到 Lucimix 的登录票据」。
// 等对接方统一了格式，可以删掉不用的那一种。
//
// 对接方给的示例对来源没有任何校验（targetOrigin 写 "*"、收消息不看 origin），
// 这里收紧成两道：消息必须来自 window.parent 本身，并且它的域名在白名单里。
// 两种信封走同样的两道校验。

// 测试环境的 Lucimix 页面在 ghznai.cn；生产是 lucimix.cn（2026-09-20 用户给出 https://lucimix.cn/chat），
// lucimix.com 是对接方早先口头确认过的，一并保留。
// 要改域名用构建时环境变量 VITE_LUCIMIX_PARENT_DOMAINS（逗号分隔），不用改代码。
const DEFAULT_PARENT_DOMAINS = "ghznai.cn,lucimix.com,lucimix.cn";

export const PARENT_DOMAINS = String(
  import.meta.env?.VITE_LUCIMIX_PARENT_DOMAINS || DEFAULT_PARENT_DOMAINS,
)
  .split(",")
  .map((domain) => domain.trim().toLowerCase())
  .filter(Boolean);

export function isEmbedded(win = window) {
  try {
    return win.parent !== win;
  } catch {
    // 跨域访问 parent 被浏览器拒绝时，说明确实被嵌在别的页面里。
    return true;
  }
}

// 域名本身或它的子域名，例如 lucimix.com 与 app.lucimix.com；
// 不能用 endsWith 直接比，否则 evillucimix.com 也会通过。
export function isAllowedParentOrigin(origin, domains = PARENT_DOMAINS) {
  let hostname;
  try {
    hostname = new URL(origin).hostname.toLowerCase();
  } catch {
    return false;
  }
  return domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
}

// ready 里没有任何敏感信息，所以拿不到可信的 referrer 时退回 "*"；
// 有白名单内的 referrer 就精确投递给它。
function readyTargetOrigin(win, domains) {
  try {
    const origin = new URL(win.document.referrer).origin;
    if (isAllowedParentOrigin(origin, domains)) return origin;
  } catch {
    // referrer 为空或被策略抹掉，走下面的兜底。
  }
  return "*";
}

// 每种信封只认它自己的字段（janus:auth 只看 token，external-app:ticket 只看 ticket）。
export function ticketFromMessage(data) {
  if (data?.type === "external-app:ticket") return data.ticket;
  if (data?.type === "janus:auth") return data.token;
  return undefined;
}

export class NoTicketError extends Error {
  constructor() {
    super("没有收到 Lucimix 的登录票据。");
    this.code = "no_ticket";
  }
}

/**
 * 向父窗口要票据。成功 resolve 票据字符串；重发用尽 reject NoTicketError；
 * 传入的 signal 被 abort 时 reject AbortError（组件卸载/重试时用）。
 * 只认第一张票据：拿到后立刻摘掉监听，后到的重复票据不会被消费。
 */
export function requestTicket({
  win = window,
  parent = win.parent,
  domains = PARENT_DOMAINS,
  resendMs = 2000,
  maxAttempts = 3,
  signal,
} = {}) {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    let timer = null;

    const settle = (settleFn, value) => {
      clearTimeout(timer);
      win.removeEventListener("message", onMessage);
      signal?.removeEventListener("abort", onAbort);
      settleFn(value);
    };
    const onMessage = (event) => {
      if (event.source !== parent) return;
      if (!isAllowedParentOrigin(event.origin, domains)) return;
      const ticket = ticketFromMessage(event.data);
      if (typeof ticket !== "string" || !ticket) return;
      settle(resolve, ticket);
    };
    const onAbort = () => settle(reject, new DOMException("已取消", "AbortError"));
    const sendReady = () => {
      if (attempts >= maxAttempts) {
        settle(reject, new NoTicketError());
        return;
      }
      attempts += 1;
      parent.postMessage({ type: "external-app:ready" }, readyTargetOrigin(win, domains));
      timer = setTimeout(sendReady, resendMs);
    };

    if (signal?.aborted) {
      onAbort();
      return;
    }
    win.addEventListener("message", onMessage);
    signal?.addEventListener("abort", onAbort);
    sendReady();
  });
}
